import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { Box, Typography, Alert } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  EditableColumnDef,
  EditableTable,
} from "../../../../../../../shared/components/table/inline-edit-table";
import {
  CustomSelect,
  CustomTextField,
} from "../../../../../../../shared/components/inputfields/form-elements";
import { StyledButton } from "../../../../../../../shared/components/buttons/custom-button";
import { ITcProductRawMaterialGeographicInformation } from "../../../../tc-models/tc-req-dto/tc-certified-product-details.req_dto";
import { useFormActions } from "../../../../../../../shared/hooks/form-builder.hooks";
import { useTransactionCertificateStore } from "../../../../../../../store/transaction-certificates.store";
import useNotification from "../../../../../../../shared/hooks/useNotification";
import { dtrackItService } from "../../../../tc-service/dtrackit.service";
import CustomTable from "../../../../../../../shared/components/table/custom-table";
import { useApi } from "../../../../../../../shared/hooks/useApi";
import { TCAppConfig } from "../../../../../../../shared/app-configs/tc-app-configs";
import { LoaderComponent } from "../../../../../../../shared/components/loaders/page-loader";
import { formatTcV4Decimal, roundTcV4Decimal } from "../../../../tc-v4-decimals";
import { makeIsCertifiedRM } from "./component-payload";

interface CountryOption {
  value: string;
  label: string;
}

interface DropdownOption {
  value: string;
  label: string;
}

interface MainRow {
  rawMaterialCode: string;
  rawMaterialCodeDesc: string;
  totalWeight: number;
  geos: ITcProductRawMaterialGeographicInformation[];
}

interface ProductGeoInfoProps {
  formRef: React.MutableRefObject<any>;
  productGeoData: ITcProductRawMaterialGeographicInformation[];
  setProductGeoData: React.Dispatch<
    React.SetStateAction<ITcProductRawMaterialGeographicInformation[]>
  >;
  productData: any;
  rawMaterialInfoData: any;
  tcStandards?: string[];
}

const ProductGeoDetailPanel: React.FC<{
  rowData: MainRow;
  countryOptions: DropdownOption[];
  onGeosUpdate: (newGeos: ITcProductRawMaterialGeographicInformation[]) => void;
  onEditStart: () => void;
  onEditEnd: () => void;
}> = ({ rowData, countryOptions, onGeosUpdate, onEditStart, onEditEnd }) => {
  const { notify } = useNotification();
  const { setRestrictPopUpSave } = useTransactionCertificateStore();
  const [subData, setSubData] = useState<ITcProductRawMaterialGeographicInformation[]>(rowData.geos);
  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
  const [stateOptionsMap, setStateOptionsMap] = useState<Record<number, DropdownOption[]>>({});

  // Refs to track state for unmount cleanup
  const subDataRef = React.useRef(subData);
  const editingIndexRef = React.useRef(editingRowIndex);

  useEffect(() => {
    subDataRef.current = subData;
    editingIndexRef.current = editingRowIndex;
  }, [subData, editingRowIndex]);

  // Cleanup on unmount (accordion close)
  useEffect(() => {
    return () => {
      const currentIndex = editingIndexRef.current;
      if (currentIndex !== -1) {
        const currentData = subDataRef.current;
        if (currentIndex < currentData.length) {
          const row = currentData[currentIndex];
          const isIncomplete = !row.tcProductCertifiedRawMaterialCountryOrArea ||
            !row.tcProductCertifiedRawMaterialStateOrProvince;

          if (isIncomplete) {
            const newData = currentData.filter((_, i) => i !== currentIndex);
            onGeosUpdate(newData);
          }
        }
        setRestrictPopUpSave(false);
        onEditEnd();
      }
    };
  }, []);

  const validationSchema = Yup.object().shape({
    tcProductCertifiedRawMaterialWeight: Yup.number()
      .min(0, "Weight must be greater than or equal to 0")
      .nullable(),
    tcProductCertifiedRawMaterialCountryOrArea: Yup.string()
      .nullable(),
    tcProductCertifiedRawMaterialStateOrProvince: Yup.string()
      .nullable(),
  });

  const subFormik = useFormik<ITcProductRawMaterialGeographicInformation>({
    initialValues: {
      tcProductCertifiedRawMaterialCode: rowData.rawMaterialCode,
      // Empty until the user types the split weight (no default 0 — 86ajey1xz)
      tcProductCertifiedRawMaterialWeight: "" as unknown as number,
      tcProductCertifiedRawMaterialCountryOrArea: "",
      tcProductCertifiedRawMaterialStateOrProvince: "",
      tcProductCertifiedRawMaterialCountryOrAreaDescription: "",
      tcProductCertifiedRawMaterialStateOrProvinceDescription: "",
      tcProductCertifiedRawMaterialCodeDescription: rowData.rawMaterialCodeDesc,
    },
    validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => { },
  });

  // Initialize state dropdowns for sub rows
  useEffect(() => {
    if (subData.length > 0 && countryOptions.length > 0) {
      const newMap: Record<number, DropdownOption[]> = {};

      subData.forEach((row, index) => {
        newMap[index] = [];

        if (row.tcProductCertifiedRawMaterialCountryOrArea) {
          getSubState(row.tcProductCertifiedRawMaterialCountryOrArea, index);
        }
      });

      setStateOptionsMap(newMap);
    }
    // getSubState is defined in component scope and uses subData, setStateOptionsMap, onGeosUpdate, notify
  }, [subData, countryOptions]);

  const normalizeCountryCode = (val?: string) => {
    if (!val) return "";
    const v = String(val).trim();
    if (v.startsWith("[")) return v;
    const m = v.match(/\(([A-Za-z0-9_-]+)\)$/);
    if (m) return m[1];
    return v;
  };

  const getSubState = async (countryCode: string, subRowIndex: number) => {
    try {
      const code = normalizeCountryCode(countryCode);
      if (!code) {
        setStateOptionsMap((prev) => ({ ...prev, [subRowIndex]: [] }));
        return [];
      }
      const result = await dtrackItService.getStateBasedonCountry(code);
      const list = Array.isArray(result?.data) ? result.data : [];
      const states = list
        .map((t: any) => {
          const code = String(t.code_1 || t.code || t.value || "").trim();
          const rawDesc = String(t.description_1 || t.name || t.label || "").replace(/\s*\([A-Za-z0-9_-]+\)$/, "").trim();
          const label = rawDesc ? `${rawDesc} (${code})` : code;
          return { value: code, label };
        })
        .filter((item: any) => Boolean(item.value && item.label))
        .sort((a: any, b: any) =>
          a.label.localeCompare(b.label, undefined, { sensitivity: "base", numeric: true })
        );
      setStateOptionsMap((prev) => ({ ...prev, [subRowIndex]: states }));
      // Backfill state description if code already present
      const currentSubRow = subData[subRowIndex];
      if (currentSubRow && currentSubRow.tcProductCertifiedRawMaterialStateOrProvince && !currentSubRow.tcProductCertifiedRawMaterialStateOrProvinceDescription) {
        const stDesc = states.find((s: { value: string; label: string }) => s.value === currentSubRow.tcProductCertifiedRawMaterialStateOrProvince)?.label || "";
        if (stDesc) {
          const updated = subData.map((r: any, i: number) => i === subRowIndex ? { ...r, tcProductCertifiedRawMaterialStateOrProvinceDescription: stDesc } : r);
          setSubData(updated);
          onGeosUpdate(updated);
        }
      }
      return states;
    } catch (err) {
      notify("Failed to fetch states", "error");
      setStateOptionsMap((prev) => ({ ...prev, [subRowIndex]: [] }));
      return [];
    }
  };

  const findNextIncompleteSubRow = () => {
   
    return -1;
  };

  const currentSum = subData.reduce((sum, geo) => sum + (geo.tcProductCertifiedRawMaterialWeight || 0), 0);
  const isMatch = Math.abs(currentSum - rowData.totalWeight) < 0.01;


  const handleSubCellChange = (subRowIndex: number, columnId: string, value: any) => {
    let parsedValue = value;
    if (columnId === "tcProductCertifiedRawMaterialWeight") {
      // Keep "" while the user is typing/clearing (a forced 0 makes the field
      // impossible to empty — 86ajey1xz); saving coerces to a number.
      parsedValue = value === "" ? "" : parseFloat(value) || 0;
    }
    subFormik.setFieldValue(columnId, parsedValue);
    let newSubData = subData.map((row, i) => (i === subRowIndex ? { ...row, [columnId]: parsedValue } : row));
    if (columnId === "tcProductCertifiedRawMaterialCountryOrArea") {
      const desc = countryOptions.find((c) => c.value === value)?.label || "";
      newSubData = newSubData.map((r, i) => (i === subRowIndex ? { ...r, tcProductCertifiedRawMaterialCountryOrAreaDescription: desc } : r));
      const cleared = newSubData.map((r, i) => (i === subRowIndex ? { ...r, tcProductCertifiedRawMaterialStateOrProvince: "", tcProductCertifiedRawMaterialStateOrProvinceDescription: "" } : r));
      newSubData = cleared;
      getSubState(value, subRowIndex);
    } else if (columnId === "tcProductCertifiedRawMaterialStateOrProvince") {
      const states = stateOptionsMap[subRowIndex] || [];
      const desc = states.find((s) => s.value === value)?.label || "";
      newSubData = newSubData.map((r, i) => (i === subRowIndex ? { ...r, tcProductCertifiedRawMaterialStateOrProvinceDescription: desc } : r));
    }
    setSubData(newSubData);
    onGeosUpdate(newSubData);
  };

  const handleSubCancelEdit = () => {
    if (editingRowIndex >= 0 && editingRowIndex < subData.length) {
      const currentRow = subData[editingRowIndex];
      const isIncomplete = !currentRow.tcProductCertifiedRawMaterialCountryOrArea || !currentRow.tcProductCertifiedRawMaterialStateOrProvince;
      if (isIncomplete) {
        const newSubData = subData.filter((_, i) => i !== editingRowIndex);
        setSubData(newSubData);
        onGeosUpdate(newSubData);
      } else {
        subFormik.setValues(currentRow);
      }
    }
    setEditingRowIndex(-1);
    onEditEnd();
    // Clear the global restriction when user cancels their explicit edit.
    setRestrictPopUpSave(false);
  };

  const handleSubSaveRow = async () => {
    const errors = await subFormik.validateForm();

    if (Object.keys(errors).length > 0) {
      Object.values(errors).forEach((err) => notify(err as string, "error"));
      return;
    }

    // Validate Country/Area and State/Province are required
    if (!subFormik.values.tcProductCertifiedRawMaterialCountryOrArea ||
      subFormik.values.tcProductCertifiedRawMaterialCountryOrArea.trim() === "") {
      notify("Country/Area is required", "error");
      return;
    }

    if (!subFormik.values.tcProductCertifiedRawMaterialStateOrProvince ||
      subFormik.values.tcProductCertifiedRawMaterialStateOrProvince.trim() === "") {
      notify("State/Province is required", "error");
      return;
    }

    // Check for duplicate country + state combination
    const isDuplicate = subData.some((row, idx) => {
      if (idx === editingRowIndex) return false; // skip the row being edited
      return (
        row.tcProductCertifiedRawMaterialCountryOrArea === subFormik.values.tcProductCertifiedRawMaterialCountryOrArea &&
        row.tcProductCertifiedRawMaterialStateOrProvince === subFormik.values.tcProductCertifiedRawMaterialStateOrProvince
      );
    });
    if (isDuplicate) {
      notify(
        "This Country and State/Province combination has already been added. Each geographic origin must be unique.",
        "error"
      );
      return;
    }

    // Check if adding this row's weight would exceed total
    const oldWeight = subData[editingRowIndex]?.tcProductCertifiedRawMaterialWeight || 0;
    const newWeight = subFormik.values.tcProductCertifiedRawMaterialWeight || 0;
    const currentSumWithoutThis = subData.reduce((sum, geo, idx) => {
      if (idx === editingRowIndex) return sum;
      return sum + (geo.tcProductCertifiedRawMaterialWeight || 0);
    }, 0);
    const projectedSum = currentSumWithoutThis + newWeight;
    if (projectedSum > rowData.totalWeight + 0.01) {
      notify("Total geographic weights cannot exceed the raw material weight.", "error");
      return;
    }

    // Ensure descriptions are populated on save
    const countryDesc = countryOptions.find((c) => c.value === subFormik.values.tcProductCertifiedRawMaterialCountryOrArea)?.label || subFormik.values.tcProductCertifiedRawMaterialCountryOrAreaDescription || "";
    const statesForRow = Array.isArray(stateOptionsMap[editingRowIndex]) ? stateOptionsMap[editingRowIndex] : [];
    const stateDesc = statesForRow.find((s) => s.value === subFormik.values.tcProductCertifiedRawMaterialStateOrProvince)?.label || subFormik.values.tcProductCertifiedRawMaterialStateOrProvinceDescription || "";
    const updatedRow = {
      ...subFormik.values,
      // The edit cell may hold "" (weights start empty) — store a number.
      tcProductCertifiedRawMaterialWeight:
        Number(subFormik.values.tcProductCertifiedRawMaterialWeight) || 0,
      tcProductCertifiedRawMaterialCountryOrAreaDescription: countryDesc,
      tcProductCertifiedRawMaterialStateOrProvinceDescription: stateDesc,
    } as ITcProductRawMaterialGeographicInformation;
    const newSubData = subData.map((row, i) => (i === editingRowIndex ? updatedRow : row));
    setSubData(newSubData);
    onGeosUpdate(newSubData);
    notify("Row saved successfully", "success");
    setEditingRowIndex(-1);
    onEditEnd();
    subFormik.resetForm();
    const nextIndex = findNextIncompleteSubRow();
    const hasIncomplete = nextIndex !== -1;
    const newCurrentSum = newSubData.reduce((sum, geo) => sum + (geo.tcProductCertifiedRawMaterialWeight || 0), 0);
    const hasMismatch = Math.abs(newCurrentSum - rowData.totalWeight) >= 0.01;
    // After an explicit save by the user, clear the global restriction so navigation is allowed.
    setRestrictPopUpSave(false);
  };

  const handleSubEditRow = (subRowIndex: number) => {
    setEditingRowIndex(subRowIndex);
    onEditStart();
    // When user starts editing explicitly, block navigation until they save/cancel.
    setRestrictPopUpSave(true);
    subFormik.setValues(subData[subRowIndex]);
  };

  const handleSubDeleteRow = (subRowIndex: number) => {
    const newSubData = subData.filter((_, i) => i !== subRowIndex);
    setSubData(newSubData);
    onGeosUpdate(newSubData);
    notify("Row deleted successfully", "success");
    if (editingRowIndex === subRowIndex) {
      setEditingRowIndex(-1);
      onEditEnd();
      subFormik.resetForm();
    }
 
    setRestrictPopUpSave(false);
  };

  const handleAddNewSubRow = () => {
    const currentSum = subData.reduce((sum, geo) => sum + (geo.tcProductCertifiedRawMaterialWeight || 0), 0);
    const isMatch = Math.abs(currentSum - rowData.totalWeight) < 0.01;
    if (isMatch) {
      notify("Total weight is already matched. Cannot add more entries.", "warning");
      return;
    }
    const newGeo: ITcProductRawMaterialGeographicInformation = {
      tcProductCertifiedRawMaterialCode: rowData.rawMaterialCode,
      // Empty until the user types the split weight (no default 0 — 86ajey1xz)
      tcProductCertifiedRawMaterialWeight: "" as unknown as number,
      tcProductCertifiedRawMaterialCountryOrArea: "",
      tcProductCertifiedRawMaterialStateOrProvince: "",
      tcProductCertifiedRawMaterialCountryOrAreaDescription: "",
      tcProductCertifiedRawMaterialStateOrProvinceDescription: "",
      tcProductCertifiedRawMaterialCodeDescription: rowData.rawMaterialCodeDesc,
    };
    const newSubData = [...subData, newGeo];
    setSubData(newSubData);
    onGeosUpdate(newSubData);
    // Auto edit the new row
    const newIndex = newSubData.length - 1;
    setEditingRowIndex(newIndex);
    onEditStart();
    subFormik.setValues(newGeo);
    setRestrictPopUpSave(true);
  };

  const subColumns: EditableColumnDef<ITcProductRawMaterialGeographicInformation>[] = useMemo(
    () => [
      {
        accessorKey: "tcProductCertifiedRawMaterialCountryOrArea",
        header: "",
        Header: (
          <span>
            Certified Raw Material<br /> Country/Area
          </span>
        ),
        Cell: ({ row: { original } }) => original.tcProductCertifiedRawMaterialCountryOrAreaDescription || "Not Selected",
        enableEditing: true,
        renderEditCell: ({ value, onChange, rowIndex: subRowIndex }) => (
          <CustomSelect
            value={value ?? ""}
            options={countryOptions}
            placeholder="Select Country/Area"
            enableSearch={true}
            onChange={(e) => {
              const selectedCountry = String(e.target.value);
              const selectedOption = countryOptions.find((opt) => opt.value === selectedCountry);
              const description = selectedOption?.label ?? "";
              onChange(selectedCountry);
              subFormik.setFieldValue("tcProductCertifiedRawMaterialCountryOrArea", selectedCountry);
              subFormik.setFieldValue("tcProductCertifiedRawMaterialCountryOrAreaDescription", description);
              subFormik.setFieldValue("tcProductCertifiedRawMaterialStateOrProvince", "");
              subFormik.setFieldValue("tcProductCertifiedRawMaterialStateOrProvinceDescription", "");
              getSubState(selectedCountry, subRowIndex);
            }}
          />
        ),
      },
      {
        accessorKey: "tcProductCertifiedRawMaterialStateOrProvince",
        header: "",
        Header: (
          <span>
            Certified Raw Material<br /> State/Province
          </span>
        ),
        Cell: ({ row: { original } }) => original.tcProductCertifiedRawMaterialStateOrProvinceDescription || "Not Selected",
        enableEditing: true,
        renderEditCell: ({ value, onChange, rowIndex: subRowIndex }) => (
          <CustomSelect
            value={value ?? ""}
            options={stateOptionsMap[subRowIndex] || []}
            placeholder="Select State/Province"
            enableSearch={true}
            onChange={(e) => {
              const selectedState = String(e.target.value);
              const statesForRow = stateOptionsMap[subRowIndex] || [];
              const description = statesForRow.find((s) => s.value === selectedState)?.label ?? "";
              onChange(selectedState);
              subFormik.setFieldValue("tcProductCertifiedRawMaterialStateOrProvince", selectedState);
              subFormik.setFieldValue("tcProductCertifiedRawMaterialStateOrProvinceDescription", description);
            }}
          />
        ),
      },
      {
        accessorKey: "tcProductCertifiedRawMaterialWeight",
        header: "",
        Header: (
          <span>
            Certified Raw Material<br /> Weight(kg)
          </span>
        ),
        enableEditing: true,
        renderEditCell: ({ value, onChange }) => (
          <CustomTextField
            name="tcProductCertifiedRawMaterialWeight"
            value={value}
            type="number"
            onChange={(e) => onChange(e.target.value)}
            error={subFormik.touched.tcProductCertifiedRawMaterialWeight && Boolean(subFormik.errors.tcProductCertifiedRawMaterialWeight)}
            helperText={subFormik.touched.tcProductCertifiedRawMaterialWeight && subFormik.errors.tcProductCertifiedRawMaterialWeight}
            sx={{ mb: 2 }}
          />
        ),
      },
    ],
    [countryOptions, stateOptionsMap, subFormik]
  );

  const sumOfAddedWeights = subData.reduce(
    (sum, item) => sum + (Number(item.tcProductCertifiedRawMaterialWeight) || 0),
    0
  );
  const remainingWeight = rowData.totalWeight - sumOfAddedWeights;
  const isFullyMatched = Math.abs(remainingWeight) < 0.01;

  return (
    <Box sx={{ p: 2, width: '100%' }}>
      <Box 
        display="flex" 
        alignItems="center" 
        justifyContent="space-between" 
        flexWrap="wrap" 
        gap={2} 
        mb={2}
        sx={{
          backgroundColor: "rgba(0, 0, 0, 0.02)",
          p: 1.5,
          borderRadius: "6px",
          border: "1px dashed rgba(0, 0, 0, 0.12)"
        }}
      >
        <Box display="flex" gap={3} flexWrap="wrap">
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Expected Weight
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {formatTcV4Decimal(rowData.totalWeight)} kg
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Total Added
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: "primary.main" }}>
              {formatTcV4Decimal(sumOfAddedWeights)} kg
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary" display="block">
              Remaining
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 600, 
                color: isFullyMatched ? "success.main" : remainingWeight < 0 ? "error.main" : "warning.main" 
              }}
            >
              {formatTcV4Decimal(remainingWeight)} kg
            </Typography>
          </Box>
        </Box>
        <StyledButton
          onClick={handleAddNewSubRow}
          size="small"
          disabled={isFullyMatched || editingRowIndex !== -1}
        >
          Add Geographic Info
        </StyledButton>
      </Box>
      <EditableTable
        data={subData.filter(row => row && Object.keys(row).length > 0)}
        columns={subColumns}
        onSaveRow={handleSubSaveRow}
        onCellChange={handleSubCellChange}
        editingRowIndex={editingRowIndex}
        onEditRow={handleSubEditRow}
        onCancelEdit={handleSubCancelEdit}
        onDeleteRow={handleSubDeleteRow}
        hideDelete={false}
        enableEditing={true}
      />
      {!isMatch && (
        <Alert severity="warning" sx={{ mt: 2 }}>
          The sum of geographic weights must exactly match the total raw material weight.
        </Alert>
      )}
    </Box>
  );
};

const ProductGeoInfo: React.FC<ProductGeoInfoProps> = ({
  formRef,
  productGeoData,
  setProductGeoData,
  productData,
  rawMaterialInfoData,
  tcStandards = [],
}) => {
  const { notify } = useNotification();
  const { updateField } = useFormActions("certified-products");
  const [mainData, setMainData] = useState<MainRow[]>([]);
  const [countryOptions, setCountryOptions] = useState<DropdownOption[]>([]);
  const [isAnySubEditing, setIsAnySubEditing] = useState(false);
  const [certifiedRawMaterialCodes, setCertifiedRawMaterialCodes] = useState<any[]>([]);
  const [tcStandardList, setTcStandardList] = useState<string[]>(tcStandards);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { executeGet } = useApi();
  const hasFetchedDataRef = useRef(false);
  const { setRestrictPopUpSave } = useTransactionCertificateStore();
  
  // Debug logging - only log when data actually changes, not on every render
  useEffect(() => {
    if (certifiedRawMaterialCodes.length > 0 || tcStandardList.length > 0) {
      console.debug("[ProductGeoInfo] State snapshot", {
        certifiedRawMaterialCodesCount: certifiedRawMaterialCodes.length,
        tcStandardList,
        rawMaterialInfoDataCount: Array.isArray(rawMaterialInfoData) ? rawMaterialInfoData.length : 0,
        productGeoDataCount: Array.isArray(productGeoData) ? productGeoData.length : 0,
      });
    }
  }, [certifiedRawMaterialCodes.length, tcStandardList.length]);

  const allWeightsMatched = useMemo(() => {
    if (mainData.length === 0) return true;
    return mainData.every((m) => {
      const geoSum = m.geos.reduce(
        (sum, geo) => sum + (Number(geo.tcProductCertifiedRawMaterialWeight) || 0),
        0
      );
      return Math.abs(geoSum - m.totalWeight) < 0.01;
    });
  }, [mainData]);

  useEffect(() => {
    setRestrictPopUpSave(!allWeightsMatched || isAnySubEditing);
    return () => {
      setRestrictPopUpSave(false);
    };
  }, [mainData, allWeightsMatched, isAnySubEditing, setRestrictPopUpSave]);

  useEffect(() => {
    if (formRef) {
      formRef.current = {
        ...formRef.current,
        editingRowIndex: (isAnySubEditing || !allWeightsMatched) ? 0 : -1,
        hasUnsavedRow: false,
      };
    }
  }, [mainData, isAnySubEditing, allWeightsMatched, formRef]);

  const handleEditStart = () => setIsAnySubEditing(true);
  const handleEditEnd = () => setIsAnySubEditing(false);

  useEffect(() => {
    setTcStandardList(Array.isArray(tcStandards) ? tcStandards : []);
  }, [tcStandards]);

  // Fetch the certified RM mapping only once on mount.
  const executeGetRef = useRef(executeGet);
  useEffect(() => {
    executeGetRef.current = executeGet;
  }, [executeGet]);

  const productGeoDataSignature = useMemo(
    () =>
      JSON.stringify(
        (Array.isArray(productGeoData) ? productGeoData : []).map((geo) => ({
          code: geo.tcProductCertifiedRawMaterialCode || "",
          weight: geo.tcProductCertifiedRawMaterialWeight || 0,
          country: geo.tcProductCertifiedRawMaterialCountryOrArea || "",
          countryDescription:
            geo.tcProductCertifiedRawMaterialCountryOrAreaDescription || "",
          state: geo.tcProductCertifiedRawMaterialStateOrProvince || "",
          stateDescription:
            geo.tcProductCertifiedRawMaterialStateOrProvinceDescription || "",
        }))
      ),
    [productGeoData]
  );

  useEffect(() => {
    if (hasFetchedDataRef.current) return;
    
    const fetchData = async () => {
      try {
        hasFetchedDataRef.current = true;
        setIsLoading(true);
        const rmRes = await executeGetRef.current(
          TCAppConfig.GET_CERTIFIED_RAW_MATERIAL_CODES
        );

        const rmList = rmRes?.data?.response || rmRes?.data || [];
        setCertifiedRawMaterialCodes(Array.isArray(rmList) ? rmList : []);

        console.debug("[ProductGeoInfo] Certified RM mapping", rmList);
      } catch (err) {
        console.error("Failed to fetch certified RM mapping", err);
        setCertifiedRawMaterialCodes([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  // Memoize isCertifiedRM to prevent recreation on every render
  const isCertifiedRM = useMemo(() => {
    return makeIsCertifiedRM(certifiedRawMaterialCodes, tcStandardList);
  }, [certifiedRawMaterialCodes, tcStandardList]);

  // Fetch countries on mount - only once
  const hasFetchedCountriesRef = useRef(false);
  useEffect(() => {
    if (hasFetchedCountriesRef.current) return;
    
    const getCountry = async () => {
      try {
        hasFetchedCountriesRef.current = true;
        const result = await dtrackItService.getCountry();
        const list = Array.isArray(result?.data) ? result.data : [];
        const countries = list
          .map((t: any) => {
            const code = String(t.code_1 || t.code || t.value || "").trim();
            const rawDesc = String(t.description_1 || t.name || t.label || "").replace(/\s*\([A-Za-z0-9_-]+\)$/, "").trim();
            const label = rawDesc ? `${rawDesc} (${code})` : code;
            return { value: code, label };
          })
          .filter((item: any) => Boolean(item.value && item.label))
          .sort((a: any, b: any) =>
            a.label.localeCompare(b.label, undefined, { sensitivity: "base", numeric: true })
          );
        setCountryOptions(countries);
      } catch (err) {
        notify("Failed to fetch countries", "error");
      }
    };
    getCountry();
  }, [notify]);

  // Generate main rows and handle existing geos - use ref to break circular dependency
  const productGeoDataRef = useRef(productGeoData);
  useEffect(() => {
    productGeoDataRef.current = productGeoData;
  }, [productGeoData]);

  useEffect(() => {
    if (Array.isArray(rawMaterialInfoData) && rawMaterialInfoData.length > 0 && productData && certifiedRawMaterialCodes.length > 0 && tcStandardList.length > 0) {
      const certifiedRawMaterials = rawMaterialInfoData.filter(
        (raw: any) => isCertifiedRM(raw.tcProductRawMaterialCode)
      );
      console.debug("[ProductGeoInfo] Certified raw materials (filtered)", certifiedRawMaterials);
      if (certifiedRawMaterials.length === 0) {
        console.debug("[ProductGeoInfo] No certified raw materials match TC standards");
      }

      const existingGeosMap = new Map<string, ITcProductRawMaterialGeographicInformation[]>();
      // Use the latest parent data so API-provided geography rows render on edit.
      const currentProductGeoData = Array.isArray(productGeoDataRef.current)
        ? productGeoDataRef.current
        : [];

      currentProductGeoData
        .filter((geo) => isCertifiedRM(geo.tcProductCertifiedRawMaterialCode))
        .forEach((geo) => {
          const code = geo.tcProductCertifiedRawMaterialCode;
          if (!existingGeosMap.has(code)) {
            existingGeosMap.set(code, []);
          }
          existingGeosMap.get(code)!.push(geo);
        });

      const generatedMainRows: MainRow[] = certifiedRawMaterials.map((raw: any) => {
        const code = raw.tcProductRawMaterialCode;
        const additivesWeight = Number(productData.tcProductAdditivesWeight || 0);
        const weight =
          (productData.tcProductNetShippingWeight - productData.tcProductSupplementaryWeight - additivesWeight) *
          (raw.tcProductRawMaterialPercentage / 100);
        const totalWeight = roundTcV4Decimal(weight);
        const existingGeos = existingGeosMap.get(code) || [];
        const geos: ITcProductRawMaterialGeographicInformation[] = existingGeos.length > 0 ? existingGeos : [];

        return {
          rawMaterialCode: code,
          rawMaterialCodeDesc: raw.tcProductRawMaterialCodeDescription || "",
          totalWeight,
          geos,
        };
      });

      setMainData(generatedMainRows);
    } else if (!isLoading && certifiedRawMaterialCodes.length === 0 && tcStandardList.length === 0) {
      // Only set empty if we've finished loading and have no data
      setMainData([]);
    }
  }, [
    rawMaterialInfoData,
    productData,
    certifiedRawMaterialCodes,
    tcStandardList,
    isCertifiedRM,
    isLoading,
    productGeoDataSignature,
  ]);

  // Backfill country descriptions when countries load - matching old logic
  useEffect(() => {
    if (countryOptions.length > 0 && mainData.length > 0) {
      let changed = false;
      const updatedMainData = mainData.map((m) => {
        let updatedGeos = m.geos.map((geo) => {
          if (geo.tcProductCertifiedRawMaterialCountryOrArea && !geo.tcProductCertifiedRawMaterialCountryOrAreaDescription) {
            const desc = countryOptions.find((c) => c.value === geo.tcProductCertifiedRawMaterialCountryOrArea)?.label || "";
            if (desc) {
              changed = true;
              return { ...geo, tcProductCertifiedRawMaterialCountryOrAreaDescription: desc };
            }
          }
          return geo;
        });
        return { ...m, geos: updatedGeos };
      });
      if (changed) {
        setMainData(updatedMainData);
      }
    }
  }, [countryOptions]);

  // Flatten and update on mainData change - use comparison to prevent unnecessary updates
  useEffect(() => {
    if (
      mainData.length === 0 &&
      Array.isArray(productGeoDataRef.current) &&
      productGeoDataRef.current.length > 0
    ) {
      return;
    }

    const flatGeos = mainData.flatMap((m) =>
      m.geos.map((g) => {
        const codeDesc = g.tcProductCertifiedRawMaterialCodeDescription;
        if (codeDesc === m.rawMaterialCodeDesc) {
          return g;
        }
        return {
          ...g,
          tcProductCertifiedRawMaterialCodeDescription: m.rawMaterialCodeDesc,
        };
      })
    );
    
    // Only update if data actually changed to prevent infinite loops
    const currentGeoData = productGeoDataRef.current;
    const hasChanged = 
      flatGeos.length !== currentGeoData.length ||
      flatGeos.some((geo, idx) => {
        const current = currentGeoData[idx];
        return !current || 
          geo.tcProductCertifiedRawMaterialCode !== current.tcProductCertifiedRawMaterialCode ||
          geo.tcProductCertifiedRawMaterialWeight !== current.tcProductCertifiedRawMaterialWeight ||
          geo.tcProductCertifiedRawMaterialCountryOrArea !== current.tcProductCertifiedRawMaterialCountryOrArea ||
          geo.tcProductCertifiedRawMaterialStateOrProvince !== current.tcProductCertifiedRawMaterialStateOrProvince;
      });
    
    if (hasChanged) {
      productGeoDataRef.current = flatGeos;
      setProductGeoData(flatGeos);
      updateField("productGeoInfo", flatGeos);
    }
  }, [mainData, setProductGeoData, updateField]);

  const columns: any[] = useMemo(
    () => [
      {
        accessorKey: "rawMaterialCode",
        header: "",
        Header: (
          <span>
            Product Certified Raw Material Code
          </span>
        ),
        enableEditing: false,
        Cell: ({ row }: { row: { original: MainRow } }) => {
          const { rawMaterialCode, rawMaterialCodeDesc } = row.original;
          return rawMaterialCodeDesc
            ? `${rawMaterialCode} - ${rawMaterialCodeDesc}`
            : rawMaterialCode;
        },
      },
      {
        accessorKey: "totalWeight",
        header: "",
        Header: (
          <span>
            Product Certified Raw Material Weight(kg)
          </span>
        ),
        enableEditing: false,
        Cell: ({ row }: { row: { original: MainRow } }) => formatTcV4Decimal(row.original.totalWeight),
      },
    ],
    [] // columns don't depend on any props or state
  );

  const handleGeosUpdate = (updatedRowCode: string, newGeos: ITcProductRawMaterialGeographicInformation[]) => {
    setMainData((prev) =>
      prev.map((m) => (m.rawMaterialCode === updatedRowCode ? { ...m, geos: newGeos } : m))
    );
  };

  if (isLoading) {
    return <LoaderComponent />;
  }

  return (
    <>
      <Typography margin={"10px"} fontWeight={"400"} fontStyle={"italic"}>
        Please expand the table to enter Country/Area, State/Province level
        certified weight
      </Typography>
      {mainData.length === 0 && (
        <Typography color="text.secondary" sx={{ mb: 1, mx: 1 }}>
          No certified raw materials match the TC standards for this product.
        </Typography>
      )}
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 1, border: "2px solid rgba(128, 128, 128, .2)", my: 4 }}
      >
        <CustomTable
          columns={columns}
          data={mainData}
          enableTopToolbar={false}
          enableBottomToolbar={false}
          enableRowActions={false}
          enableExpanding={true}
          renderDetailPanel={({ row }: { row: { original: MainRow } }) => (
            <Box sx={{ width: '100%', p: 0 }}>
              <ProductGeoDetailPanel
                rowData={row.original}
                countryOptions={countryOptions}
                onGeosUpdate={(newGeos) => handleGeosUpdate(row.original.rawMaterialCode, newGeos)}
                onEditStart={handleEditStart}
                onEditEnd={handleEditEnd}
              />
            </Box>
          )}
        />
      </Box>
    </>
  );
};

export default ProductGeoInfo;
